import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../../dataManagement/uiDb/UiDb";
import { selectDineInBoardLoadingMap } from "../../../../../../dataManagement/uiDb/DineInBoardDb";
import DineInBoardThunk from "../../../../../../services/thunks/DineInBoardThunk";
import DineInThunk from "../../../../../../services/thunks/DineInThunk";
import {
  DINE_IN_ITEM_STATUS,
  DINE_IN_ROUND_STATUS,
  toWireDineInItemStatus,
} from "../../../../../../services/helpers/DineInSummaryHelper";
import { useNotification } from "../../../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../../../constants/Enums";
import { DineInKitchenItem, DineInKitchenRound } from "./dineInDetailBuilder";

// ─── Dine In board — kitchen actions ──────────────────────────────────────
// Every mutation the kitchen screen offers goes through this one hook, so the
// components stay presentational and there is a single place to look when
// asking "what can this screen change, and how".
//
// Accept, reject, serve, mark-round-served and generateBill are all LIVE.
//
// Two vocabularies are in play and they are NOT interchangeable, even where
// they overlap. Items go through toWireDineInItemStatus (ACCEPTED / REJECTED /
// SERVED); rounds use DINE_IN_ROUND_STATUS directly (APPROVED / REJECTED /
// SERVED). They agree on SERVED and REJECTED and differ on the rest, which is
// exactly the situation where borrowing one for the other goes unnoticed.
//
// Statuses go out through toWireDineInItemStatus, never as raw literals: the
// app compares against canonical values internally while the API uses its own
// spellings (SERVED here is DELIVERED on the wire).

/** Reasons offered as one tap. Typing a reason mid-service is the enemy. */
export const DINE_IN_REJECT_REASONS = [
  "Out of stock",
  "Kitchen closed",
  "Takes too long",
] as const;

/** Which button a decision in flight belongs to, so only that button spins. */
export type DineInBoardBusyAction = "accept" | "reject" | "serve" | "acceptRound" | "serveRound";

export interface DineInBoardActions {
  acceptItem: (item: DineInKitchenItem) => void;
  /** Opens the reason prompt. The rejection itself is confirmRejection. */
  requestReject: (item: DineInKitchenItem) => void;
  cancelReject: () => void;
  confirmReject: (reason: string) => void;
  /** The item awaiting a reason, for the modal to render against. */
  rejectTarget: DineInKitchenItem | null;
  /** True while any decision call is in flight — buttons disable off this. */
  isDeciding: boolean;
  /** True for the one button whose decision is in flight — it shows a spinner. */
  isBusy: (key: string, action: DineInBoardBusyAction) => boolean;

  /** Accepts every item in the round still awaiting a decision, in one call. */
  acceptRound: (round: DineInKitchenRound) => void;

  deliverItem: (item: DineInKitchenItem) => void;
  deliverRound: (round: DineInKitchenRound) => void;
  /**
   * Confirmed from the bill review modal. Closing the table out and billing it
   * are the SAME act: the bill endpoint closes the session server-side and
   * returns the dine-in order, which then shows up in the Orders tab.
   */
  generateBill: (sessionId: string) => void;
}

export function useDineInBoardActions(businessId: string): DineInBoardActions {
  const dispatch = useDispatch<AppDispatch>();
  const { triggerNotification, startLoading, stopLoading } = useNotification();
  const loadingMap = useSelector(selectDineInBoardLoadingMap);
  // The button whose decision is in flight. Replaces the full-screen overlay
  // for decisions: that one button spins, and every other decision button is
  // disabled until it settles (isDeciding), so a second decision can't be
  // taken against stale statuses.
  const [busy, setBusy] = useState<{ key: string; action: DineInBoardBusyAction } | null>(null);
  const isDeciding = busy != null || !!loadingMap.decideItems;

  const [rejectTarget, setRejectTarget] = useState<DineInKitchenItem | null>(null);

  /**
   * Shared path for every decision.
   *
   * Marks the tapped button busy for the duration. Every decision button is
   * disabled meanwhile (isDeciding), which is the point rather than a side
   * effect: a decision can mean several sequential requests (see the thunk),
   * and during that window the rows on screen still show pre-decision
   * statuses. Leaving them tappable invites a second decision against stale
   * state — the same dish accepted twice, or accepted and then rejected.
   *
   * setBusy(null) sits in `finally`, so a thrown error can't leave a button
   * spinning forever.
   *
   * On failure it refetches rather than trusting local state: a decision list
   * can fail halfway, leaving some items decided and some not, and the only
   * honest picture of that is the server's.
   */
  const decide = useCallback(
    async (
      item: DineInKitchenItem,
      { status, note, action }: { status: string; note?: string; action: DineInBoardBusyAction }
    ) => {
      if (item.sourceItemIds.length === 0) {
        triggerNotification(
          "This item can't be updated yet — reload and try again.",
          NotificationType.ERROR
        );
        return;
      }
      setBusy({ key: item.key, action });
      try {
        await dispatch(
          DineInBoardThunk.decideDineInItems({
            roundItemIds: item.sourceItemIds,
            status,
            note,
          })
        ).unwrap();
      } catch (error) {
        triggerNotification(
          typeof error === "string" ? error : "Couldn't update that item.",
          NotificationType.ERROR
        );
        dispatch(DineInBoardThunk.refreshDineInBoardSessions({ businessId }));
      } finally {
        setBusy(null);
      }
    },
    [dispatch, triggerNotification, businessId]
  );

  /**
   * Round-level twin of `decide`. Same busy-button and refetch-on-failure
   * contract, one request instead of several. The server applies a round
   * decision to the round's undecided items too (approve accepts them, serve
   * serves the accepted ones), so item statuses stay in step.
   */
  const decideRound = useCallback(
    async (
      round: DineInKitchenRound,
      { status, note, action }: { status: string; note?: string; action: DineInBoardBusyAction }
    ) => {
      setBusy({ key: round.roundId, action });
      try {
        await dispatch(
          DineInBoardThunk.decideDineInRound({ roundId: round.roundId, status, note })
        ).unwrap();
      } catch (error) {
        triggerNotification(
          typeof error === "string" ? error : "Couldn't update that round.",
          NotificationType.ERROR
        );
        dispatch(DineInBoardThunk.refreshDineInBoardSessions({ businessId }));
      } finally {
        setBusy(null);
      }
    },
    [dispatch, triggerNotification, businessId]
  );

  /**
   * Generates the bill under the same blocking overlay as a decision, so the
   * table can't be acted on mid-close. Success refreshes the board (the closed
   * table drops off once the backend lists only ACTIVE sessions); failure
   * shows the friendly message and refreshes too, since "items still waiting"
   * means local state was stale.
   */
  const generateBill = useCallback(
    async (sessionId: string) => {
      startLoading("Generating the bill");
      try {
        await dispatch(DineInThunk.generateBill({ sessionId })).unwrap();
        triggerNotification("Bill generated", NotificationType.SUCCESS);
      } catch (error) {
        triggerNotification(
          typeof error === "string" ? error : "Couldn't generate the bill.",
          NotificationType.ERROR
        );
      } finally {
        stopLoading();
        dispatch(DineInBoardThunk.refreshDineInBoardSessions({ businessId }));
      }
    },
    [dispatch, triggerNotification, startLoading, stopLoading, businessId]
  );

  // Explicit generic so the handler params below get contextual types from
  // DineInBoardActions instead of falling back to implicit any.
  return useMemo<DineInBoardActions>(
    () => ({
      acceptItem: (item) => {
        void decide(item, {
          status: toWireDineInItemStatus(DINE_IN_ITEM_STATUS.ACCEPTED),
          action: "accept",
        });
      },

      // Same endpoint as accept/reject, one status further along. No
      // confirmation: marking food as served is routine, frequent, and
      // trivially corrected by the kitchen if mis-tapped.
      deliverItem: (item) => {
        void decide(item, {
          status: toWireDineInItemStatus(DINE_IN_ITEM_STATUS.SERVED),
          action: "serve",
        });
      },

      // Rejecting is two steps on purpose. It's the destructive half of this
      // screen and the only one the diner sees a reason for, so it gets a
      // deliberate confirmation where accept gets none.
      requestReject: (item) => setRejectTarget(item),
      cancelReject: () => setRejectTarget(null),
      confirmReject: (reason: string) => {
        const item = rejectTarget;
        if (!item) return;
        setRejectTarget(null);
        void decide(item, {
          status: toWireDineInItemStatus(DINE_IN_ITEM_STATUS.REJECTED),
          note: reason.trim() || undefined,
          action: "reject",
        });
      },
      rejectTarget,
      isDeciding,
      isBusy: (key, action) => busy?.key === key && busy.action === action,

      // Round endpoint with APPROVED: the server accepts every item still
      // waiting, leaving ones already decided individually as they are.
      acceptRound: (round) => {
        void decideRound(round, {
          status: DINE_IN_ROUND_STATUS.APPROVED,
          action: "acceptRound",
        });
      },

      // One call for the whole round, not a loop over its items — that's the
      // point of the round endpoint existing. Uses the ROUND vocabulary
      // directly; it deliberately does not borrow the item mapper, since the
      // two lists only happen to agree on this value.
      deliverRound: (round) => {
        void decideRound(round, {
          status: DINE_IN_ROUND_STATUS.SERVED,
          action: "serveRound",
        });
      },
      generateBill: (sessionId) => {
        void generateBill(sessionId);
      },
    }),
    [decide, decideRound, generateBill, rejectTarget, isDeciding, busy]
  );
}

export default useDineInBoardActions;
